import { subscribe } from 'valtio';
import { BRIGHT_COLORS } from '~/constants/color';
import { imageFullscreenState } from '~/pages/projectDetails/civildraw/widgets/ObjectDetails/ImagePreview/ImageFullscreen';
import { imageHoverState } from '~/pages/projectDetails/civildraw/widgets/ObjectDetails/ImagePreview/ImageHoverPreview';
import type { ImageObject as MapImageObject } from '../../../../common/types';
import * as ImageCache from '../../ImageCache';
import BBoxObject from '../bbox/BBoxObject';
import CanvasObject from '../CanvasObject';
import { opacityWhileDragging } from '../common';
import * as geometry from '../common/geometry';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import { getBBoxCoordsWhileMinimized, HOVER_DELAY, SVG_WIDTH, svgPath } from './common';

export default class EditImageObject extends CanvasObject {
  image: MapImageObject;
  imageCanvas: CanvasImageSource | null = null;
  _onImageUpdated: (p: LngLat[]) => void;

  box: Point[];
  refBox: Point[];
  vertices: LngLat[] = [];

  bbox: BBoxObject;

  isDragging: boolean = false;

  minimizedIcon: Path2D = new Path2D(svgPath);

  isMinimized: boolean;

  hoverTimeout: NodeJS.Timeout | null = null;
  iconCenter: Point = { x: NaN, y: NaN };

  constructor(id: string, surface: SurfaceBase, image: MapImageObject, onImageUpdated: (p: LngLat[]) => void) {
    super(id, surface);

    this.image = image;
    this.interactive = true;
    this._onImageUpdated = onImageUpdated;
    this.isMinimized = this.image.properties.minimized;

    this.cleanupFns.push(
      subscribe(this.getStore(), () => {
        const image = this.getCurrentImageObject();
        this.handleImageMinimizedMode(image);
        this.renderAll();
      }),
    );

    this.refBox = this.box = image.geometry.coordinates[0].slice(0, -1).map(([lng, lat]) => {
      this.vertices.push({ lng, lat });
      return this.surface.toScreenCoords({ lng, lat });
    });

    this.bbox = new BBoxObject(`bbox-${id}`, surface, this);
    this.isMinimized === false && this.addChild(this.bbox);

    ImageCache.getImage(image.files![0].path).then((imageCanvas) => {
      this.imageCanvas = imageCanvas;
      this.renderAll();
    });

    this.on('hoverStart', () => {
      if (this.isMinimized) {
        this.hoverTimeout = setTimeout(() => {
          const image = this.getCurrentImageObject();
          const file = image?.files?.[0];
          if (!file) return;
          const lngLat = this.surface.toMapCoords(this.iconCenter);
          imageHoverState.setParams(file, this.iconCenter, lngLat);
        }, HOVER_DELAY);
      }
    });

    this.on('hoverEnd', () => {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
        imageHoverState.reset();
      }
    });

    this.on('tap', (e) => {
      this.emit('hoverEnd', e);
      const image = this.getCurrentImageObject();
      const file = image?.files?.[0];
      if (!file) return;
      imageFullscreenState.setImage(image.id, image.title || 'Image', file);
    });

    this.on('scaleStart', () => {
      this.box = this.bbox.getBBoxCoords();
    });

    this.on('scaling', () => {
      this.box = this.bbox.getBBoxCoords();
      this.renderAll();
    });

    this.on('scaleEnd', () => {
      this.box = this.bbox.getBBoxCoords();
      this.refBox = this.box;
      this.onImageUpdated();
      this.renderAll();
    });

    this.on('rotateStart', () => {
      this.refBox = this.box;
    });

    this.on('rotating', (e) => {
      this.box = geometry.polygonRotate(this.refBox, e.angle, geometry.polygonCentroid(this.refBox));

      this.renderAll();
    });

    this.on('rotateEnd', () => {
      this.onImageUpdated();
    });

    this.on('dragStart', (e) => {
      this.emit('hoverEnd', e);
      this.isDragging = true;
      this.refBox = this.box;
      this.bbox.emit('dragStart', e);
    });

    this.on('dragging', (e) => {
      const { x, y } = e.diff();
      this.box = this.refBox.map((c) => ({
        x: c.x + x,
        y: c.y + y,
      }));
      this.bbox.emit('dragging', e);
      this.renderAll();
    });
    this.on('dragEnd', (e) => {
      this.isDragging = false;
      this.bbox.emit('dragEnd', e);
      this.onImageUpdated();
    });
  }

  getCurrentImageObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'image') return;
    return currentObject;
  }

  handleImageMinimizedMode = (imageObject?: MapImageObject) => {
    if (!imageObject) return;

    if (this.isMinimized !== imageObject.properties.minimized) {
      this.isMinimized = imageObject.properties.minimized;

      if (this.isMinimized === true) {
        this.removeChild(this.bbox);
        return;
      }

      // trigger bbox handle refresh so the coords are updated upon render
      this.bbox.refreshScreenCoords();
      this.addChild(this.bbox);
      return;
    }
  };

  onImageUpdated = () => {
    const coords = this.box.map((p) => this.surface.toMapCoords(p));
    this.vertices = coords;
    this._onImageUpdated([...coords, coords[0]]);
  };

  getBBoxCoords = (): Point[] => {
    return this.box;
  };

  getCenter = (): Point => {
    return geometry.polygonCentroid(this.box);
  };

  refreshScreenCoords(): void {
    super.refreshScreenCoords();
    this.box = this.vertices.map((v) => this.surface.toScreenCoords(v));
  }

  renderIcon = (ctx: CanvasRenderingContext2D) => {
    const image = this.getCurrentImageObject();
    if (!image) return;
    const { properties, title } = image;

    const [_, __, ___, p] = this.box;
    this.iconCenter = { x: p.x + SVG_WIDTH / 2, y: p.y - SVG_WIDTH / 2 };
    const [p1, p2, p3, p4] = getBBoxCoordsWhileMinimized(this.iconCenter);

    ctx.save();
    ctx.translate(p.x, p.y - SVG_WIDTH);
    ctx.fillStyle = properties.color || 'red';
    ctx.fill(this.minimizedIcon);
    ctx.restore();

    ctx.save();
    ctx.lineWidth = 1;
    ctx.strokeStyle = properties.color || 'red';
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.lineTo(p3.x, p3.y);
    ctx.lineTo(p4.x, p4.y);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();

    if (properties.showTitle === true) {
      const padding = 10;
      const fontSize = 14;
      const labelOffset = padding;

      ctx.save();
      ctx.font = `bold ${fontSize}px arial`;
      const textWidth = ctx.measureText(title).width;

      const pt = {
        x: (p1.x + p2.x) / 2 - textWidth / 2 - padding / 2,
        y: p.y - SVG_WIDTH - labelOffset - fontSize - padding,
      };

      const w = textWidth + padding;
      const h = fontSize + padding;

      ctx.fillStyle = properties.color || 'red';
      ctx.beginPath();
      ctx.roundRect(pt.x, pt.y, w, h, [5]);
      ctx.fill();

      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';
      ctx.fillText(title, pt.x + padding / 2, pt.y + padding / 2);
      ctx.restore();
      return;
    }
  };

  render(ctx: CanvasRenderingContext2D) {
    const image = this.getCurrentImageObject();
    if (!image) return;
    const { properties } = image;

    if (properties.minimized === true) {
      this.renderIcon(ctx);
      return;
    }

    this.bbox.render(
      ctx,
      properties.color,
      properties.strokeWidth,
      properties.strokeStyle === 'dashed',
      properties.strokeOpacity,
      !properties.showBorder,
    );

    if (this.imageCanvas) {
      const [p1, p2, p3] = this.box;

      ctx.beginPath();
      const center = this.getCenter();
      const width = geometry.distance(p1, p2);
      const height = geometry.distance(p2, p3);

      const angle = geometry.lineAngle(p1, p2);
      ctx.save();
      ctx.translate(center.x, center.y);
      ctx.rotate(angle);
      ctx.translate(-center.x, -center.y);

      ctx.strokeStyle = properties.color;

      ctx.globalAlpha = properties.fillOpacity;

      if (this.isDragging && ctx.globalAlpha > opacityWhileDragging) {
        ctx.globalAlpha = opacityWhileDragging;
      }

      ctx.drawImage(this.imageCanvas, center.x - width / 2, center.y - height / 2, width, height);
      ctx.globalAlpha = 1;
      ctx.lineWidth = properties.strokeWidth;

      this.surface.resetCanvasTransform(ctx);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.setLineDash([]);
      const [p1, p2, p3, p4] = this.image.geometry.coordinates[0]
        .slice(0, -1)
        .map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));
      const center = geometry.polygonCentroid([p1, p2, p3, p4]);
      ctx.font = '16px arial';
      ctx.strokeText('Loading...', center.x - 24, center.y);
    }
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    const image = this.getCurrentImageObject();
    if (!image) return;
    const { properties } = image;

    if (properties.minimized === true) {
      const [p1, p2, p3] = getBBoxCoordsWhileMinimized(this.iconCenter);
      ctx.save();
      ctx.lineWidth = 1;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.fillRect(p1.x, p1.y, p2.x - p1.x, p3.y - p1.y);
      ctx.restore();

      return;
    }

    ctx.beginPath();
    ctx.moveTo(this.box[0].x, this.box[0].y);
    ctx.lineTo(this.box[1].x, this.box[1].y);
    ctx.lineTo(this.box[2].x, this.box[2].y);
    ctx.lineTo(this.box[3].x, this.box[3].y);
    ctx.fillStyle = color;
    ctx.fill();
  }
}
